import type { components } from "@cntrl/api-client";
import { useShows } from "../../../app/hooks/useShows.js";
import { useSharedShows } from "../../../app/hooks/useShowShares.js";
import { upcomingSharedShows } from "../../booking/lib/bookingRows.js";
import { todayIso } from "./homeFormat.js";

/**
 * The upcoming/tonight show a per-show home card previews (ticket sales / VIP / travel). There is no band-level
 * "next show" endpoint, so resolve the soonest CONFIRMED show from the shows list, then the card fetches per-show
 * (are all `{showId}`-routed). Born-blank when the band has no upcoming confirmed show.
 */
export function useUpcomingShow() {
  const q = useShows({ status: "confirmed", sort: "date", from: todayIso() });
  const next = q.data?.items?.[0];
  return {
    showId: next?.showId,
    show: next,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: () => void q.refetch(),
  };
}

type ShowSummary = components["schemas"]["ShowSummary"];

/**
 * The band's upcoming CONFIRMED dates INCLUDING the ones it plays as a guest (an accepted support-slot share on a
 * confirmed host date, ART-249/251) — soonest first. A guest date lives on the HOST band's spine, so the own-band
 * list never carries it. Fail-soft on the shared half: a shared-shows error must not take the band's own dates
 * down. Deliberately NOT what {@link useUpcomingShow} resolves: the per-show cards (ticket sales) read routes a
 * guest cannot reach yet.
 */
export function useUpcomingShowsWithShared() {
  const own = useShows({ status: "confirmed", sort: "date", from: todayIso() });
  const shared = useSharedShows();
  const guest = upcomingSharedShows(shared.data, todayIso());
  const items: ShowSummary[] = [...(own.data?.items ?? []), ...guest]
    .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  return {
    items,
    isLoading: own.isLoading,
    isError: own.isError,
    error: own.error,
    refetch: () => { void own.refetch(); void shared.refetch(); },
  };
}
